import styled from '@emotion/styled'
import { Fragment, useEffect, useMemo } from '@wordpress/element'
import { applyFilters } from '@wordpress/hooks'
import { __ } from '@wordpress/i18n'
import { Icon, Price, Tooltip } from '@wptravelengine/public/fragments'
import { useQuery } from 'react-query'

const { BlockTitle, ModalSection, PricingCategory, SkeletonCategories, Counter } = window.wptravelengine?.activityComponents || {};

const SelectExtraServices = ({ state, setState, data, isFetching: _isFetching, dates, packages, ...rest }) => {
    const { summary: { extraServices, selectedPackage, selectedDate, travelers } } = state
    const totalTravelers = travelers?.reduce((total, traveler) => total + traveler.qty, 0)
    const { tripID } = data
    const { data: rawServices = [], isLoading: isServiceLoading } = useQuery(['extraservices', tripID, selectedDate], () => fetch(`${wpApiSettings.root}wptravelengine/v2/trips/${tripID}/services${selectedDate ? `?date=${selectedDate}` : ''}`).then(r => r.json()))
    const isFetching = _isFetching || isServiceLoading

    const services = useMemo(
        () => (Array.isArray(rawServices) ? rawServices : []).map((service, index) =>
            applyFilters('wptravelengine.extraServices.service', service, { index })
        ),
        [rawServices]
    )

    const updateSummary = (newSummary) => {
        setState(prev => ({
            ...prev,
            summary: {
                ...prev.summary,
                ...newSummary
            }
        }))
    }

    useEffect(() => {
        if (extraServices.length === 0 && selectedDate) {
            const extraServices = services.flatMap(({ id, required, options, multiple }) => {
                const mapOption = option => ({
                    service_id: id,
                    id: option.key,
                    qty: 0,
                    label: option.label,
                    title: option.title,
                    unitPrice: option.price,
                    perTextLabel: ` / ${option.serviceUnit.value}`,
                    perTextDescription: option.description,
                    total: 0,
                    required
                });

                const isSoldOut = option => Number.isInteger(option?.avaQty) && option.avaQty === 0
                const defaultOption = options.find(option => !isSoldOut(option)) || options[0]

                return multiple ? options.map(mapOption) : options.length ? [mapOption(defaultOption)] : [];
            });
            updateSummary({ extraServices });
        }
    }, [services, selectedDate])

    // Core's own initial population (App.jsx) always defaults a non-multiple service to its
    // first option regardless of stock, and runs before this component's own populate effect
    // above ever gets a chance (extraServices is no longer empty by then). If the current
    // selection is sold out for THIS date but a different option isn't, switch to it — but only
    // while qty is still 0, so we never override a quantity the user has actually set.
    useEffect(() => {
        if (!selectedDate || !services.length) return

        const isSoldOut = option => Number.isInteger(option?.avaQty) && option.avaQty === 0
        let changed = false

        const corrected = extraServices.map(es => {
            if (es.qty !== 0) return es
            const service = services.find(s => s.id === es.service_id)
            if (!service || service.multiple) return es
            const currentOption = service.options.find(o => o.key === es.id)
            if (!currentOption || !isSoldOut(currentOption)) return es
            const betterOption = service.options.find(o => !isSoldOut(o))
            if (!betterOption || betterOption.key === es.id) return es

            changed = true
            return {
                ...es,
                id: betterOption.key,
                label: betterOption.label,
                title: betterOption.title,
                unitPrice: betterOption.price,
                perTextLabel: ` / ${betterOption.serviceUnit.value}`,
                perTextDescription: betterOption.description,
            }
        })

        if (changed) updateSummary({ extraServices: corrected })
    }, [services, extraServices, selectedDate])

    // proceed to checkout
    // Core's own check (App.jsx) intentionally skips required-service validation when this addon
    // is active (it defers to us via the expectedVersion gate) and doesn't watch extraServices at
    // all in that case. So instead of ANDing against core's last value (which can only ever
    // downgrade it and can never re-raise it once a required service is later fulfilled), we
    // independently recompute the full formula here using our own date-aware services.
    useEffect(() => {
        const requiredServiceIds = services?.filter(service => service.required)?.map(service => service.id) || []
        const allRequiredServicesSelected = requiredServiceIds.every(serviceId => {
            const serviceOptions = extraServices.filter(service => service.service_id === serviceId)
            return serviceOptions.some(service => service.qty > 0)
        })

        const selectedTripPackage = packages?.find(({ id }) => id === selectedPackage)
        const minPax = selectedTripPackage?.min_pax || 1
        const requiredTravelers = totalTravelers >= minPax
        const packageDates = dates?.find(d => d.package.id === selectedPackage)?.dates
        const availableTimes = packageDates?.[selectedDate]?.times?.filter(time => (time.seats === '' || time.seats >= totalTravelers))

        const corrected = !!(requiredTravelers && allRequiredServicesSelected && selectedDate &&
            (!availableTimes?.length || state.summary.selectedTime) && totalTravelers > 0)

        setState(prev => prev.proceedToCheckout === corrected ? prev : { ...prev, proceedToCheckout: corrected })
    }, [services, extraServices, selectedPackage, selectedDate, totalTravelers, dates, packages, state.summary.selectedTime])

    const handleUpdateQuantity = (id, qty) => {
        updateSummary({
            extraServices: extraServices?.map(service => {
                if (service.id === id) {
                    const total = service?.unitPrice * qty
                    return { ...service, qty, total }
                }
                return service
            })
        })
    }

    const handleUpdateKey = (id, key) => {
        updateSummary({
            extraServices: extraServices.map(service => {
                if (service.service_id === id) {
                    const newOption = services.find(s => s.id === id).options.find(o => o.key === key)
                    return {
                        ...service,
                        id: key,
                        unitPrice: newOption.price,
                        title: newOption.title,
                        qty: 0,
                        total: 0,
                    }
                }
                return service
            })
        })
    }

    const allSoldOut = Array.isArray(services) && services.length > 0 &&
        services.every(service => service.options?.every(opt => Number.isInteger(opt?.avaQty) && opt.avaQty === 0))

    if (!selectedPackage || !selectedDate || extraServices.length === 0 || totalTravelers === 0) return null;
    if (allSoldOut && !applyFilters('wptravelengine.extraServices.showAllSoldOut', false, { services })) return null;

    const filteredServices = services?.filter(service => applyFilters('wptravelengine.extraServices.renderService', true, { service }))

    return (
        <>
            {filteredServices?.length > 0 && <>
                <BlockTitle style={{ minHeight: 34 }}>{!isFetching && (wteL10n?.l10n?.extraServicesTitle || __("Extra Services", "wptravelengine-activity-tour-booking"))}</BlockTitle>
                <ModalSection>
                    {isFetching ? <SkeletonCategories /> : filteredServices.map(({ id, required, title, options, multiple, type }, index) => {
                        const _service = extraServices.filter(service => service.service_id === id);
                        const isCustomType = type === 'custom';
                        const showTitle = isCustomType;
                        // Check if next service is default type (to add border)
                        const nextService = services[index + 1];
                        const needsBorder = isCustomType && nextService?.type === 'default';

                        const content = (
                            <>
                                {showTitle ? (
                                    <ServiceTitle>
                                        {title}{required && <span className='required'>*</span>}
                                    </ServiceTitle>
                                ) : null}
                                {_service.map(({ label, id: _id, unitPrice, qty }) => {
                                    const selectedOption = options.find(option => option.key === _id);
                                    const perTextLabel = selectedOption ? ` / ${selectedOption.serviceUnit.label}` : '';
                                    const perTextDescription = selectedOption?.description;
                                    const my_label = selectedOption?.title || selectedOption?.label || label
                                    const displayLabel = showTitle ? my_label : (!multiple && options.length === 1 ? my_label : (multiple ? my_label : (selectedOption?.title || title)));
                                    const showRequired = required && !showTitle
                                    const maxQty = Number.isInteger(selectedOption?.avaQty) ? selectedOption.avaQty : Infinity

                                    const defaultItem = (
                                        <PricingCategory key={`${id}-${_id}`}>
                                            <div className='category__labels'>
                                                <span className='category__label'>
                                                    <span dangerouslySetInnerHTML={{ __html: displayLabel }} />{showRequired && <span className='required'>*</span>}
                                                    {!multiple && options.length > 1 && <select onChange={(e) => handleUpdateKey(id, e.target.value)} value={_id}>
                                                        {options.map(({ key: _key, label: _label, avaQty = '' }) => {
                                                            if (Number.isInteger(avaQty) && avaQty <= 0) return null
                                                            return <option value={_key} key={_key}>{_label}</option>
                                                        })}
                                                    </select>}
                                                </span>
                                                <span className='category__price'>
                                                    <Price value={unitPrice} noHTML />{perTextLabel}
                                                    {perTextDescription && <Tooltip content={<div dangerouslySetInnerHTML={{ __html: perTextDescription }}></div>} >
                                                        <span style={{ marginLeft: "4px" }} className="wte-meta-help"><Icon icon="help" /></span>
                                                    </Tooltip>}
                                                </span>
                                            </div>
                                            <Counter
                                                value={qty}
                                                max={maxQty}
                                                onChange={qty => handleUpdateQuantity(_id, qty)}
                                            />
                                        </PricingCategory>
                                    );

                                    return applyFilters(
                                        'wptravelengine.extraServices.activityOptionItem',
                                        defaultItem,
                                        { option: selectedOption, label: displayLabel, price: unitPrice, perTextLabel, perTextDescription, required: showRequired }
                                    );
                                })}
                            </>
                        );

                        return isCustomType ? (
                            <ServiceGroup key={id} $hasBorder={needsBorder}>{content}</ServiceGroup>
                        ) : (
                            <Fragment key={id}>{content}</Fragment>
                        );
                    })}
                </ModalSection>
            </>}
        </>
    )
}

export default SelectExtraServices

const ServiceGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${({ $hasBorder }) => $hasBorder && `
        padding-bottom: 12px;
        border-bottom: 1px solid #e8e8e8;
    `}
`

const ServiceTitle = styled.h5`
    margin: 0;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    &::after{
        content: "";
        width: 100%;
        height: 1px;
        background: #e8e8e8;
        flex: 1;
    }
`
